// Every browser resource belongs to one mounted landing instance.
export function createMotionScope() {
  const cleanups = [],
    frames = new Set(),
    timers = new Set(),
    animations = new Set();
  let disposed = false;
  function listen(target, type, callback, options) {
    target.addEventListener(type, callback, options);
    cleanups.push(() => target.removeEventListener(type, callback, options));
  }
  function frame(callback) {
    if (disposed) return;
    const id = requestAnimationFrame((time) => {
      frames.delete(id);
      if (!disposed) callback(time);
    });
    frames.add(id);
    return id;
  }
  function delay(callback, duration) {
    const id = setTimeout(() => {
      timers.delete(id);
      if (!disposed) callback();
    }, duration);
    timers.add(id);
    return id;
  }
  function animate(element, keyframes, options) {
    const animation = element.animate(keyframes, options);
    animations.add(animation);
    animation.finished.then(
      () => animations.delete(animation),
      () => animations.delete(animation)
    );
    return animation;
  }
  function observeSize(element, callback) {
    const observer = new ResizeObserver(() => {
      if (!disposed) callback();
    });
    observer.observe(element);
    cleanups.push(() => observer.disconnect());
    return observer;
  }
  function observeReveal(callback, options) {
    const observer = new IntersectionObserver((entries) => {
      if (!disposed) callback(entries);
    }, options);
    cleanups.push(() => observer.disconnect());
    return observer;
  }
  function dispose() {
    disposed = true;
    cleanups.forEach((cleanup) => cleanup());
    frames.forEach(cancelAnimationFrame);
    timers.forEach(clearTimeout);
    animations.forEach((animation) => animation.cancel());
    cleanups.length = 0;
    frames.clear();
    timers.clear();
    animations.clear();
  }
  return { listen, frame, delay, animate, observeSize, observeReveal, dispose };
}
